import { useState, useMemo } from "react";
import type { UserItem } from "@/mocks/configuracoes";
import SettingsStatusBadge from "./SettingsStatusBadge";

interface UsersTableProps {
  users: UserItem[];
  onNewUser?: () => void;
}

export default function UsersTable({ users, onNewUser }: UsersTableProps) {
  const [search, setSearch] = useState("");
  const [filterProfile, setFilterProfile] = useState("");

  const profiles = useMemo(() => {
    const set = new Set(users.map((u) => u.profile));
    return Array.from(set).sort();
  }, [users]);

  const filtered = useMemo(() => {
    return users.filter((u) => {
      if (search) {
        const q = search.toLowerCase();
        if (!u.name.toLowerCase().includes(q) && !u.email.toLowerCase().includes(q)) return false;
      }
      if (filterProfile && u.profile !== filterProfile) return false;
      return true;
    });
  }, [users, search, filterProfile]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
          <input type="text" placeholder="Buscar por nome ou e-mail..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none" />
        </div>
        <select value={filterProfile} onChange={(e) => setFilterProfile(e.target.value)} className="px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none">
          <option value="">Todos os perfis</option>
          {profiles.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        {onNewUser && (
          <button onClick={onNewUser} className="px-4 py-2 text-sm font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap">
            <i className="ri-user-add-line w-4 h-4 inline-flex items-center justify-center mr-1" />Novo usuário
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-background-200">
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Nome</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">E-mail</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Perfil</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Organização</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Último acesso</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Status</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                <td className="py-2.5 px-3 text-foreground-900 font-medium">{u.name}</td>
                <td className="py-2.5 px-3 text-foreground-500 text-xs">{u.email}</td>
                <td className="py-2.5 px-3 text-foreground-700 text-xs">{u.profile}</td>
                <td className="py-2.5 px-3 text-foreground-600 text-xs max-w-[140px] truncate">{u.organization}</td>
                <td className="py-2.5 px-3 text-foreground-500 text-xs">{u.lastAccess}</td>
                <td className="py-2.5 px-3"><SettingsStatusBadge status={u.status} /></td>
                <td className="py-2.5 px-3">
                  <div className="flex items-center gap-1">
                    <button className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center text-foreground-400 hover:text-primary-600 transition-colors" title="Editar">
                      <i className="ri-edit-line text-sm" />
                    </button>
                    <button className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center text-foreground-400 hover:text-danger-600 transition-colors" title="Bloquear">
                      <i className="ri-lock-line text-sm" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}